import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { useLoaderData, useMatches } from 'react-router';
import { type RequestContext, type SerializeFrom } from '@cx-web/domain-types';
import { getRepubMetaFromRepub } from '@cx-web/utils';
import { getDefaultProduct } from '@cx-web/utils/getDefaultProduct';
import { captureException } from '@sentry/react-router';

import BettingInsights from '~/components/BettingInsights';
import SortingLayout from '~/components/BettingInsights/components/SortingLayout';
import { Error as ErrorComponent } from '~/components/error/Error.tsx';
import { Hero } from '~/components/hero/Hero.tsx';
import SearchWrapper from '~/components/SearchWrapper';
import { type BettingSplitsIndex } from '~/configuration/types';
import { createBettingSplitsConfig } from '~/pages/config';
import { bettingSplitsIndex } from '~/search-domain/indexes';

import type { loader as rootLoader } from '../../../root-loader';

import styles from './styles/PublicBettingData.module.css';
import { loader } from './loader';
import { meta } from './meta';

export default function PublicBettingDataRoute() {
  const matches = useMatches();
  const rootMatch = matches.find(match => /oddschecker-plus\/root$/.test(match.id));
  const rootLoaderData = rootMatch?.data as SerializeFrom<typeof rootLoader>;
  if (!rootLoaderData) {
    throw new Error('Root loader data is missing');
  }

  const { geolocationInfo, siteConfig } = rootLoaderData;

  const {
    availableProducts,
    currentPath,
    monthlyProducts,
    repub,
    requestContext,
    resourcesUrl,
    subscriptionStatus,
    yearlyProducts,
  } = useLoaderData<typeof loader>();
  const config = createBettingSplitsConfig(requestContext as RequestContext);

  const userHasCancelledSubscription = requestContext.user.subscriptions.some(
    ({ status }: { status: string }) => status === 'CANCELLED',
  );
  const userHasActiveSubscription = requestContext.user.subscriptions.some(
    ({ status }: { status: string }) => status === 'ACTIVE' || status === 'TRIAL',
  );

  const defaultMonthlyProduct = getDefaultProduct(monthlyProducts);
  const defaultYearlyProduct = getDefaultProduct(yearlyProducts);

  const { t } = useTranslation();

  const { repubPath } = getRepubMetaFromRepub(repub);

  return (
    <SortingLayout>
      <Hero
        currentPath={currentPath}
        hasActiveSubscription={userHasActiveSubscription}
        hasCancelledSubscription={userHasCancelledSubscription}
        monthlyProduct={defaultMonthlyProduct}
        repub={repub}
        repubPath={repubPath}
        shouldHideDetailsMobile={true}
        yearlyProduct={defaultYearlyProduct}
      >
        <>
          <h1>{t('CX_PLUS_BS_TITLE')}</h1>
          <h2>{t('CX_PLUS_BS_HERO_TITLE')}</h2>

          <h3>
            {t('CX_PLUS_BS_SUB')}
            <b>{t('CX_PLUS_BS_TITLE')}</b>
          </h3>
          <p>{t('CX_PLUS_BS_HERO_HELPER_TEXT')}</p>
          <span aria-label="Powered by Sport Radar" className={styles.poweredBySportRadar}>
            <span>{t('CX_PLUS_BS_POWERED_BY')}</span>
            <img alt="SportsRadar" src={`${resourcesUrl}/assets/img/plus/powered-by-sports-radar.svg`} />
          </span>
        </>
      </Hero>

      <ErrorBoundary
        fallbackRender={({ error }) => {
          captureException(error);
          return <ErrorComponent indexName={bettingSplitsIndex} message={error.message} title="Something went wrong" />;
        }}
      >
        <SearchWrapper<BettingSplitsIndex>
          config={config}
          indexName={bettingSplitsIndex}
          repub={repub}
          state={geolocationInfo.subdivisionCode}
          userTZ={requestContext.user.TZ}
        >
          <BettingInsights<BettingSplitsIndex>
            availableProducts={availableProducts}
            bookmakers={siteConfig.bookmakers}
            config={config}
            currentPath={currentPath}
            indexName={bettingSplitsIndex}
            redirectPath={currentPath ?? null}
            resourcesUrl={resourcesUrl}
            subscriptionStatus={subscriptionStatus}
            userHasCancelledSubscription={userHasCancelledSubscription}
            wallType={config.wallType}
          />
        </SearchWrapper>
      </ErrorBoundary>
    </SortingLayout>
  );
}

export { loader, meta };
